/**
 * Premium Wall - Lean Launch Upgrade Prompt
 * 2-tier paid: Pro ($29/mo), Elite ($79/mo)
 */
import React, { useState } from 'react';
import { Crown, Lock, TrendingUp, Zap, Shield, Users, X, Server, Cpu, Bell, BarChart3, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { getBackendBaseUrl } from '../lib/apiBase';
import { toast } from 'sonner';

const API_URL = getBackendBaseUrl();

const PLANS = [
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    color: '#00d4ff',
    badge: 'Most Popular',
    description: 'Full analytics for serious DeFi traders',
    features: [
      '500 yield pools',
      'Security scores (GoPlus)',
      'Time-to-Profit charts',
      'Full Simulator Labs',
      'Portfolio Tracker',
      '3 email alerts/day',
      '7-day free trial',
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    price: 79,
    color: '#ffa502',
    badge: 'Best Value',
    description: 'The complete DeFi intelligence suite',
    features: [
      'Unlimited yield pools',
      'Everything in Pro',
      'Whale Tracker (smart money)',
      'AI Pool Insights',
      'Advanced Backtesting',
      'Forecast Dial (Monte Carlo)',
      'Telegram + unlimited alerts',
      'CSV data export',
    ],
  },
];

export default function PremiumWall({ feature = 'Whale Tracker', onClose }) {
  const { isAuthenticated } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [loadingPlan, setLoadingPlan] = useState(null);

  const handleSubscribe = async (planId) => {
    if (!isAuthenticated) {
      setShowAuthModal(true);
      return;
    }
    setLoadingPlan(planId);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ package_id: planId, origin_url: window.location.origin, payment_method: 'card' }),
      });
      const data = await res.json();
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch {
      toast("Couldn’t start checkout. Please try again.");
    }
    setLoadingPlan(null);
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] flex items-start justify-center p-4 md:p-8 overflow-y-auto">
      <div className="max-w-3xl w-full py-8">
        {/* Header */}
        <div className="text-center mb-8">
          {onClose && (
            <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          )}
          <div className="inline-flex items-center gap-2 bg-[#00ff88]/10 border border-[#00ff88]/20 rounded-full px-4 py-1.5 mb-4">
            <Lock className="w-4 h-4 text-[#00ff88]" />
            <span className="text-sm text-[#00ff88] font-medium">Upgrade Required — {feature}</span>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Unlock ProfitSpot Intelligence</h1>
          <p className="text-gray-400">
            Powered by dual-node infrastructure: <span className="text-[#ffa502]">Nighton</span> + Balinas
          </p>
        </div>

        {/* Node Power Callout */}
        <div className="grid grid-cols-2 gap-3 mb-8">
          <div className="bg-[#1a1a2e] border border-amber-500/20 rounded-xl p-4 flex items-start gap-3">
            <Cpu className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-400">Nighton (Mac Mini)</p>
              <p className="text-xs text-gray-500">Intelligence Engine — ML scoring, backtesting, smart alerts</p>
            </div>
          </div>
          <div className="bg-[#1a1a2e] border border-[#00ff88]/20 rounded-xl p-4 flex items-start gap-3">
            <Server className="w-5 h-5 text-[#00ff88] flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-[#00ff88]">Balinas Mini</p>
              <p className="text-xs text-gray-500">Data Aggregator — live feeds, gas data, yield polling</p>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-[#0f0f1a] border rounded-xl p-5 flex flex-col ${
                plan.badge ? 'border-[#00d4ff]/40 shadow-[0_0_20px_rgba(0,212,255,0.08)]' : 'border-gray-800'
              }`}
              data-testid={`premium-wall-plan-${plan.id}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-[#00d4ff] text-black text-xs font-bold rounded-full whitespace-nowrap">
                  {plan.badge}
                </div>
              )}

              <div className="mb-4">
                <h3 className="text-base font-bold text-white">{plan.name}</h3>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-3xl font-bold" style={{ color: plan.color }}>${plan.price}</span>
                  <span className="text-gray-500 text-sm">/mo</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">{plan.description}</p>
              </div>

              {plan.nighton && (
                <div className="flex items-center gap-1.5 mb-3 text-[10px] text-amber-400 bg-amber-500/10 rounded-lg px-2 py-1">
                  <Cpu className="w-3 h-3" />
                  <span>Nighton-powered features</span>
                </div>
              )}
              {plan.dualnode && (
                <div className="flex items-center gap-1.5 mb-3 text-[10px] text-[#00ff88] bg-[#00ff88]/10 rounded-lg px-2 py-1">
                  <Zap className="w-3 h-3" />
                  <span>Dual-node: Nighton + Balinas</span>
                </div>
              )}

              <ul className="space-y-2 flex-1 mb-5">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-gray-300">
                    <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: plan.color }} />
                    {f}
                  </li>
                ))}
              </ul>

              <button
                onClick={() => handleSubscribe(plan.id)}
                disabled={loadingPlan === plan.id}
                className="w-full py-2.5 rounded-lg font-bold text-sm transition-all disabled:opacity-50"
                style={{
                  backgroundColor: plan.badge ? plan.color : 'transparent',
                  color: plan.badge ? '#000' : plan.color,
                  border: `1px solid ${plan.color}40`,
                }}
                data-testid={`premium-wall-subscribe-${plan.id}`}
              >
                {loadingPlan === plan.id ? 'Loading...' : isAuthenticated ? `Get ${plan.name}` : 'Sign In to Subscribe'}
              </button>
            </div>
          ))}
        </div>

        {/* Trust signals */}
        <p className="text-center text-xs text-gray-600">
          Secure payments via Stripe • Cancel anytime • Instant access after payment
        </p>
      </div>

      {showAuthModal && <AuthModal onClose={() => setShowAuthModal(false)} />}
    </div>
  );
}

const AuthModal = ({ onClose }) => {
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') await login(email, password);
      else await register(email, password, name);
      onClose();
      window.location.reload();
    } catch (err) {
      console.error('Auth error:', err);
    }
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-4" data-testid="auth-modal">
      <div className="bg-[#1a1a2e] max-w-sm w-full rounded-xl p-6 border border-gray-700">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-white">{mode === 'login' ? 'Sign In' : 'Create Account'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <input data-testid="auth-name" value={name} onChange={e => setName(e.target.value)} placeholder="Name" className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          )}
          <input data-testid="auth-email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" type="email" required className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          <input data-testid="auth-password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" type="password" required className="w-full bg-[#0f0f1a] border border-gray-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-[#00ff88]" />
          <button data-testid="auth-submit" type="submit" disabled={loading} className="w-full bg-[#00ff88] text-black font-bold py-2.5 rounded-lg disabled:opacity-50">
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Register'}
          </button>
        </form>
        <p className="text-center text-sm text-gray-400 mt-4">
          {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
          <button onClick={() => setMode(mode === 'login' ? 'register' : 'login')} className="text-[#00ff88] hover:underline" data-testid="auth-toggle">
            {mode === 'login' ? 'Register' : 'Sign In'}
          </button>
        </p>
      </div>
    </div>
  );
};
